import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Get token from URL params (new Google OAuth flow)
        const token = searchParams.get("token");
        const plan = searchParams.get("plan");
        const isNew = searchParams.get("new");
        
        // Also check for legacy Emergent flow with session_id in hash
        const hash = window.location.hash;
        const hashParams = new URLSearchParams(hash.replace("#", ""));
        const sessionId = hashParams.get("session_id");

        if (token) {
          // New Google OAuth flow - token directly from backend
          localStorage.setItem("token", token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          
          // Get user data
          try {
            const userResponse = await axios.get(`${API}/api/auth/me`);
            localStorage.setItem("user", JSON.stringify(userResponse.data));
            
            // If new user with plan, redirect to Stripe
            if (isNew === "true" && plan) {
              toast.success("¡Cuenta creada! Redirigiendo al pago...");
              
              try {
                const stripeResponse = await axios.post(
                  `${API}/api/payments/create-checkout?plan_id=${plan}`
                );
                
                if (stripeResponse.data?.url) {
                  // Fire InitiateCheckout - redirecting to Stripe
                  if (window.fbq) {
                    const planPrices = { basico: 299, pro: 599, premium: 1099, agencia: 3999 };
                    window.fbq('trackSingle', '2770682049943810', 'InitiateCheckout', {
                      content_name: plan,
                      value: planPrices[plan] || 0,
                      currency: 'MXN'
                    });
                  }
                  window.location.href = stripeResponse.data.url;
                  return;
                }
              } catch (stripeError) {
                console.error("Stripe error:", stripeError);
                toast.error("Error al conectar con Stripe. Puedes elegir tu plan desde precios.");
              }
              navigate("/precios");
            } else if (userResponse.data?.subscription_status === "pending_payment") {
              toast.info("Elige un plan para activar tu cuenta");
              navigate("/precios");
            } else {
              toast.success(`¡Bienvenido, ${userResponse.data?.name?.split(" ")[0]}!`);
              navigate("/admin/dashboard");
            }
          } catch (userError) {
            console.error("Error getting user:", userError);
            toast.error("Error al obtener datos del usuario");
            navigate("/login");
          }
          
        } else if (sessionId) {
          // Legacy Emergent flow
          const pendingPlan = localStorage.getItem("pending_plan");
          
          const response = await fetch(`${API}/api/auth/google/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id: sessionId }),
          });

          if (!response.ok) throw new Error("Authentication failed");

          const data = await response.json();

          if (data.access_token) {
            localStorage.setItem("token", data.access_token);
            localStorage.setItem("user", JSON.stringify(data.user));
            axios.defaults.headers.common["Authorization"] = `Bearer ${data.access_token}`;
          }

          window.history.replaceState(null, "", window.location.pathname);

          if (pendingPlan) {
            localStorage.removeItem("pending_plan");
            toast.success("¡Cuenta creada! Redirigiendo al pago...");
            
            try {
              const stripeResponse = await axios.post(
                `${API}/api/payments/create-checkout?plan_id=${pendingPlan}`
              );
              
              if (stripeResponse.data?.url) {
                window.location.href = stripeResponse.data.url;
                return;
              }
            } catch (stripeError) {
              console.error("Stripe error:", stripeError);
              toast.error("Error al conectar con Stripe.");
            }
            navigate("/precios");
          } else if (data.user?.subscription_status === "pending_payment") {
            toast.info("Elige un plan para activar tu cuenta");
            navigate("/precios");
          } else {
            toast.success(`¡Bienvenido, ${data.user?.name?.split(" ")[0]}!`);
            navigate("/admin/dashboard");
          }
          
        } else {
          // No token or session_id - check URL for error
          const error = searchParams.get("error");
          if (error) {
            toast.error("Error en la autenticación con Google");
          }
          navigate("/login");
        }

      } catch (error) {
        console.error("Auth callback error:", error);
        localStorage.removeItem("pending_plan");
        toast.error("Error al iniciar sesión con Google");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-purple mx-auto mb-4"></div>
        <p className="text-white text-lg">Iniciando sesión con Google...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
